import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PhotoUtilService {

  constructor() {
  }

  private isHeic(file: File): boolean {
    return /image\/hei[cf]/i.test(file?.type ?? '') || /\.hei[cf]$/i.test(file?.name ?? '');
  }

  private imageType(file: File): string {
    const type = (file.type ?? '').toLowerCase();

    if (type.startsWith('image/')) {
      return type === 'image/jpg' ? 'image/jpeg' : type;
    }

    const extension = (file.name?.split('.').pop() ?? '').toLowerCase();

    return {png: 'image/png', gif: 'image/gif', webp: 'image/webp'}[extension] ?? 'image/jpeg';
  }

  resizeImage(fileToBeUploaded: File): Observable<File> {
    const MAX_LENGTH = 1920;

    return new Observable<File>((observer) => {

      if (!fileToBeUploaded) {
        observer.error(new Error('No file provided to resizeImage'));
        return;
      }

      if (this.isHeic(fileToBeUploaded)) {
        observer.next(fileToBeUploaded);
        observer.complete();
        return;
      }

      const imageType = this.imageType(fileToBeUploaded);
      const img = document.createElement('img');
      const objectUrl = URL.createObjectURL(fileToBeUploaded);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const scale = MAX_LENGTH / Math.max(img.width, img.height);

          if (scale >= 1) {
            observer.next(fileToBeUploaded);
            observer.complete();
            return;
          }

          // BRIEFD-7644: draw straight into a target-size canvas. A full-resolution intermediate
          // (~48 MB for 12 MP) per photo exceeded iOS WebKit's total canvas memory cap, so
          // getContext/toBlob returned null and the photo was dropped before it was ever sent.
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);

          const context = canvas.getContext('2d');
          if (!context) {
            observer.error(new Error(`Failed to resize image ${fileToBeUploaded.name}`));
            return;
          }
          context.imageSmoothingQuality = 'high';
          context.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((resizedBlob) => {
            // iOS frees canvas memory lazily; zero it now so the next photo has room.
            canvas.width = canvas.height = 0;
            try {
              if (!resizedBlob) {
                observer.error(new Error(`Failed to encode resized image for ${fileToBeUploaded.name}`));
                return;
              }
              const resizedFile = new File([resizedBlob], fileToBeUploaded.name, {type: imageType});
              observer.next(resizedFile);
              observer.complete();
            } catch (e) {
              observer.error(e);
            }
          }, imageType, 0.8);
        } catch (e) {
          observer.error(e);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        observer.error(new Error(`Failed to decode image ${fileToBeUploaded.name}`));
      };

      img.src = objectUrl;

      return () => {
        img.onload = null;
        img.onerror = null;
        URL.revokeObjectURL(objectUrl);
      };
    });
  }
}
